import { useState, useEffect, useCallback, useMemo } from 'react';
import { calendarAPI, leadsAPI, propertiesAPI, teamAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Phone, Mail, Calendar as CalendarIcon,
  Building2, MessageSquare, FileText, Clock, CheckCircle2, AlertTriangle,
  Plus, Sparkles, Users, Plane, Briefcase, MapPin, Edit2, Trash2, Lock, Repeat, X, User
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  call: { icon: Phone, color: 'bg-blue-500', label: 'Call' },
  email: { icon: Mail, color: 'bg-violet-500', label: 'Email' },
  meeting: { icon: CalendarIcon, color: 'bg-amber-500', label: 'Meeting' },
  visit: { icon: Building2, color: 'bg-emerald-500', label: 'Visit' },
  message: { icon: MessageSquare, color: 'bg-pink-500', label: 'Message' },
  note: { icon: FileText, color: 'bg-gray-500', label: 'Note' },
  follow_up: { icon: Clock, color: 'bg-orange-500', label: 'Opvolging' },
};

// Calendar Event types met kleur + icoon
const EVENT_TYPE_CONFIG = {
  workshop:  { icon: Sparkles,   label: 'Workshop',  hex: '#10B981', bg: 'bg-emerald-500', soft: 'bg-emerald-100 text-emerald-700' },
  meeting:   { icon: Users,      label: 'Overleg',   hex: '#0EA5E9', bg: 'bg-sky-500',     soft: 'bg-sky-100 text-sky-700' },
  customer:  { icon: Briefcase,  label: 'Klant',     hex: '#8B5CF6', bg: 'bg-violet-500',  soft: 'bg-violet-100 text-violet-700' },
  leave:     { icon: Plane,      label: 'Vrij',      hex: '#9CA3AF', bg: 'bg-gray-400',    soft: 'bg-gray-100 text-gray-700' },
  other:     { icon: CalendarIcon, label: 'Overig',  hex: '#F59E0B', bg: 'bg-amber-500',   soft: 'bg-amber-100 text-amber-700' },
};

const DAYS = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const MONTHS = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

function getMonthDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const days = [];
  for (let i = firstDay - 1; i >= 0; i--) days.push({ day: daysInPrev - i, month: month - 1, year, isCurrentMonth: false });
  for (let i = 1; i <= daysInMonth; i++) days.push({ day: i, month, year, isCurrentMonth: true });
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) days.push({ day: i, month: month + 1, year, isCurrentMonth: false });
  return days;
}

function dateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function parseEventDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() };
}

function fmtTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function toISO(dateStr, timeStr) {
  // dateStr=yyyy-mm-dd, timeStr=HH:MM. Construeer lokale tijd → ISO
  if (!dateStr) return null;
  const t = timeStr || '00:00';
  const d = new Date(`${dateStr}T${t}`);
  if (isNaN(d)) return null;
  return d.toISOString();
}

function toDateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function toTimeInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [activities, setActivities] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [events, setEvents] = useState([]);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteScope, setDeleteScope] = useState('single'); // 'single' | 'series'
  const navigate = useNavigate();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const fetchAll = useCallback(async () => {
    const start = new Date(year, month - 1, 1).toISOString();
    const end = new Date(year, month + 2, 0).toISOString();
    try {
      const res = await calendarAPI.getEvents({ start, end });
      setActivities(res.data.activities || []);
      setFollowUps(res.data.follow_ups || []);
      setEvents(res.data.events || []);
    } catch (err) { console.error(err); }
  }, [year, month]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Build event map by date
  const eventMap = useMemo(() => {
    const map = {};
    const add = (key, ev) => {
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    };
    events.forEach(e => {
      const d = parseEventDate(e.start_at);
      if (d) add(dateKey(d.year, d.month, d.day), { ...e, kind: 'event' });
    });
    activities.forEach(a => {
      const d = parseEventDate(a.event_date || a.created_at);
      if (d) add(dateKey(d.year, d.month, d.day), { ...a, kind: 'activity' });
    });
    followUps.forEach(f => {
      const d = parseEventDate(f.event_date || f.due_date);
      if (d) add(dateKey(d.year, d.month, d.day), { ...f, kind: 'follow_up' });
    });
    return map;
  }, [activities, followUps, events]);

  const monthDays = getMonthDays(year, month);
  const today = new Date();
  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate());
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();
  const selectedKey = selectedDate
    ? dateKey(selectedDate.year, selectedDate.month, selectedDate.day)
    : (isCurrentMonth ? todayKey : null);
  const selectedEvents = selectedKey ? (eventMap[selectedKey] || []) : [];

  const prevMonth = () => { setCurrentDate(new Date(year, month - 1, 1)); setSelectedDate(null); };
  const nextMonth = () => { setCurrentDate(new Date(year, month + 1, 1)); setSelectedDate(null); };
  const goToday = () => { setCurrentDate(new Date()); setSelectedDate(null); };

  const openNewEvent = () => {
    setEditingEvent(null);
    setShowEventDialog(true);
  };

  const openEditEvent = (ev) => {
    setEditingEvent(ev);
    setShowEventDialog(true);
  };

  const handleDeleteEvent = async () => {
    if (!confirmDelete) return;
    try {
      const params = confirmDelete.is_recurring ? { scope: deleteScope } : undefined;
      await calendarAPI.deleteEvent(confirmDelete.id, params);
      toast.success(confirmDelete.is_recurring && deleteScope === 'single' ? 'Deze instantie verwijderd' : 'Event verwijderd');
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const openDeleteDialog = (ev) => {
    setDeleteScope('single');
    setConfirmDelete(ev);
  };

  return (
    <div className="space-y-4 fade-in" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Agenda</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Events, activiteiten en opvolgingen</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" onClick={openNewEvent} className="text-xs h-8 gap-1" data-testid="new-event-btn">
            <Plus className="w-3.5 h-3.5" /> Nieuw event
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} className="text-xs h-8" data-testid="cal-today-btn">Vandaag</Button>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={prevMonth} className="h-8 w-8 p-0" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-semibold w-36 text-center">{MONTHS[month]} {year}</span>
            <Button variant="ghost" size="sm" onClick={nextMonth} className="h-8 w-8 p-0" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4">
        {/* Grid */}
        <div className="flex-1">
          {/* Desktop Grid */}
          <div className="hidden sm:block">
            <Card className="border-border/60 overflow-hidden">
              <div className="grid grid-cols-7">
                {DAYS.map(d => (
                  <div key={d} className="py-2 text-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b bg-muted/30">{d}</div>
                ))}
                {monthDays.map((d, i) => {
                  const key = dateKey(d.year, d.month, d.day);
                  const cell = eventMap[key] || [];
                  const isToday = key === todayKey;
                  const isSelected = key === selectedKey;
                  const cellEvents = cell.filter(e => e.kind === 'event');
                  const fuCount = cell.filter(e => e.kind === 'follow_up').length;
                  const actCount = cell.filter(e => e.kind === 'activity').length;
                  const hasOverdue = cell.some(e => e.kind === 'follow_up' && e.status === 'pending' && new Date(e.due_date) < new Date());

                  return (
                    <button
                      key={i}
                      onClick={() => setSelectedDate({ year: d.year, month: d.month, day: d.day })}
                      className={`relative min-h-[90px] p-1.5 border-b border-r text-left transition-colors hover:bg-muted/30 ${
                        !d.isCurrentMonth ? 'bg-muted/10' : ''
                      } ${isSelected ? 'bg-primary/5 ring-1 ring-primary/30' : ''}`}
                    >
                      <span className={`text-xs font-medium ${
                        !d.isCurrentMonth ? 'text-muted-foreground/40' :
                        isToday ? 'text-white bg-primary rounded-full w-6 h-6 flex items-center justify-center' :
                        'text-foreground'
                      }`}>
                        {d.day}
                      </span>
                      {d.isCurrentMonth && (cellEvents.length > 0 || fuCount > 0 || actCount > 0) && (
                        <div className="mt-1 space-y-0.5">
                          {/* Events: tonen tot 2, dan +N */}
                          {cellEvents.slice(0, 2).map(ev => {
                            const cfg = EVENT_TYPE_CONFIG[ev.event_type] || EVENT_TYPE_CONFIG.other;
                            return (
                              <div key={ev.id} className="flex items-center gap-1 text-[9px] rounded px-1 py-0.5 text-white truncate"
                                style={{ background: ev.color || cfg.hex }}
                                title={ev.title + (ev.is_recurring ? ' (herhalend)' : '')}>
                                <cfg.icon className="w-2.5 h-2.5 flex-shrink-0" />
                                <span className="truncate font-medium">{ev.title}</span>
                                {ev.is_recurring && <Repeat className="w-2.5 h-2.5 flex-shrink-0 opacity-80" />}
                              </div>
                            );
                          })}
                          {cellEvents.length > 2 && (
                            <div className="text-[9px] text-muted-foreground px-1">+{cellEvents.length - 2} meer</div>
                          )}
                          {fuCount > 0 && (
                            <div className={`flex items-center gap-1 text-[9px] rounded px-1 py-0.5 ${hasOverdue ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' : 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'}`}>
                              <Clock className="w-2.5 h-2.5" />{fuCount} opvolg{fuCount > 1 ? 'ingen' : 'ing'}
                            </div>
                          )}
                          {actCount > 0 && (
                            <div className="flex items-center gap-1 text-[9px] bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded px-1 py-0.5">
                              <CalendarIcon className="w-2.5 h-2.5" />{actCount} activiteit{actCount > 1 ? 'en' : ''}
                            </div>
                          )}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Mobile Week */}
          <div className="sm:hidden">
            <MobileWeekView
              currentDate={currentDate}
              selectedKey={selectedKey}
              todayKey={todayKey}
              eventMap={eventMap}
              onSelectDate={(d) => setSelectedDate(d)}
              onPrevWeek={() => setCurrentDate(new Date(currentDate.getTime() - 7 * 86400000))}
              onNextWeek={() => setCurrentDate(new Date(currentDate.getTime() + 7 * 86400000))}
            />
          </div>
        </div>

        {/* Day Detail Panel */}
        <div className="lg:w-[360px]">
          <Card className="border-border/60 sticky top-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold">
                  {selectedDate
                    ? new Date(selectedDate.year, selectedDate.month, selectedDate.day).toLocaleDateString('nl-NL', { weekday: 'long', month: 'long', day: 'numeric' })
                    : isCurrentMonth ? 'Vandaag' : 'Selecteer een datum'
                  }
                </h3>
                <Badge variant="secondary" className="text-[10px]">{selectedEvents.length} items</Badge>
              </div>

              {selectedKey && (
                <Button size="sm" variant="outline" onClick={() => {
                  // Open new event prefilled with date
                  setEditingEvent({ __prefillDate: selectedKey });
                  setShowEventDialog(true);
                }} className="w-full mb-3 gap-1 h-8 text-xs" data-testid="add-on-date-btn">
                  <Plus className="w-3 h-3" /> Event op deze dag
                </Button>
              )}

              {selectedEvents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarIcon className="w-8 h-8 mx-auto mb-2 opacity-20" />
                  <p className="text-xs">
                    {!selectedKey ? 'Klik op een datum' : 'Geen items op deze dag'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {/* Events first */}
                  {selectedEvents.filter(e => e.kind === 'event').sort((a,b) => (a.start_at || '').localeCompare(b.start_at || '')).map(ev => {
                    const cfg = EVENT_TYPE_CONFIG[ev.event_type] || EVENT_TYPE_CONFIG.other;
                    const Icon = cfg.icon;
                    return (
                      <div key={ev.id} className="p-2.5 rounded-lg border border-border/40 hover:bg-muted/30 transition-colors group" data-testid={`cal-event-item-${ev.id}`}>
                        <div className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 text-white"
                            style={{ background: ev.color || cfg.hex }}>
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-semibold">{ev.title}</span>
                              {ev.is_private && <Lock className="w-3 h-3 text-muted-foreground" />}
                              {ev.is_recurring && (
                                <span title="Herhalend event" className="inline-flex items-center text-muted-foreground">
                                  <Repeat className="w-3 h-3" />
                                </span>
                              )}
                              <Badge variant="secondary" className="text-[8px] h-4">{cfg.label}</Badge>
                            </div>
                            <div className="mt-0.5 text-[10px] text-muted-foreground flex items-center gap-2 flex-wrap">
                              {ev.all_day ? (
                                <span>Hele dag</span>
                              ) : (
                                <span className="font-mono">{fmtTime(ev.start_at)}{ev.end_at ? ` – ${fmtTime(ev.end_at)}` : ''}</span>
                              )}
                              {ev.location && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{ev.location}</span>}
                            </div>
                            {ev.lead_name && (
                              <button onClick={() => navigate(`/leads/${ev.lead_id}`)} className="text-[10px] text-primary hover:underline mt-0.5 block">Lead: {ev.lead_name}</button>
                            )}
                            {ev.property_title && (
                              <button onClick={() => navigate(`/properties/${ev.property_id}`)} className="text-[10px] text-primary hover:underline mt-0.5 block">Dienst: {ev.property_title}</button>
                            )}
                            {ev.description && <p className="text-[10px] text-muted-foreground/80 mt-1 line-clamp-2">{ev.description}</p>}
                            <p className="text-[9px] text-muted-foreground/60 mt-1">Door {ev.owner_name || 'iemand'}</p>
                          </div>
                          {ev.is_owner && (
                            <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openEditEvent(ev)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-event-${ev.id}`}><Edit2 className="w-3 h-3" /></button>
                              <button onClick={() => openDeleteDialog(ev)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-event-${ev.id}`}><Trash2 className="w-3 h-3" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Follow-ups */}
                  {selectedEvents.filter(e => e.kind === 'follow_up').map(ev => {
                    const isOverdue = ev.status === 'pending' && new Date(ev.due_date) < new Date();
                    return (
                      <button key={ev.id} onClick={() => navigate(`/leads/${ev.lead_id}`)}
                        className="w-full text-left p-2.5 rounded-lg border border-border/40 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start gap-2.5">
                          <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                            isOverdue ? 'bg-red-100 dark:bg-red-900/30' :
                            ev.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30' :
                            'bg-orange-100 dark:bg-orange-900/30'
                          }`}>
                            {ev.status === 'completed' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                              : isOverdue ? <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                              : <Clock className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium capitalize">Opvolging: {ev.type}</span>
                              <Badge variant={isOverdue ? 'destructive' : ev.status === 'completed' ? 'default' : 'secondary'} className="text-[8px] h-4">
                                {isOverdue ? 'Verlopen' : ev.status === 'completed' ? 'Afgerond' : 'Open'}
                              </Badge>
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">{ev.lead_name}</p>
                            {ev.notes && <p className="text-[10px] text-muted-foreground/70 mt-0.5 line-clamp-1">{ev.notes}</p>}
                            <p className="text-[9px] text-muted-foreground/50 mt-1 font-mono">{fmtTime(ev.due_date)}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {/* Activities */}
                  {selectedEvents.filter(e => e.kind === 'activity').map(ev => {
                    const cfg = TYPE_CONFIG[ev.type] || TYPE_CONFIG.note;
                    const Icon = cfg.icon;
                    const hex = { 'bg-blue-500':'#3B82F6','bg-violet-500':'#8B5CF6','bg-amber-500':'#F59E0B','bg-emerald-500':'#10B981','bg-pink-500':'#EC4899','bg-gray-500':'#6B7280' }[cfg.color] || '#6B7280';
                    return (
                      <button key={ev.id} onClick={() => navigate(`/leads/${ev.lead_id}`)}
                        className="w-full text-left p-2.5 rounded-lg border border-border/40 hover:bg-muted/30 transition-colors">
                        <div className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${hex}15` }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: hex }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium">{cfg.label}</span>
                              {ev.outcome && <Badge variant="secondary" className="text-[8px] h-4">{ev.outcome?.replace(/_/g, ' ')}</Badge>}
                            </div>
                            <p className="text-[11px] text-muted-foreground mt-0.5">{ev.lead_name}</p>
                            {ev.notes && <p className="text-[10px] text-muted-foreground/70 mt-0.5 line-clamp-1">{ev.notes}</p>}
                            <p className="text-[9px] text-muted-foreground/50 mt-1 font-mono">{fmtTime(ev.created_at)}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Workshop</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-sky-500" />Overleg</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-violet-500" />Klant</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-gray-400" />Vrij</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-amber-500" />Overig</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-orange-400" />Opvolging</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-red-500" />Verlopen</div>
        <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-sm bg-blue-500" />Activiteit</div>
      </div>

      <EventDialog
        open={showEventDialog}
        onClose={() => { setShowEventDialog(false); setEditingEvent(null); }}
        event={editingEvent}
        onSaved={() => { fetchAll(); setShowEventDialog(false); setEditingEvent(null); }}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Event verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.is_recurring
                ? <>"{confirmDelete?.title}" is onderdeel van een terugkerende serie. Kies wat je wilt verwijderen:</>
                : <>Weet je zeker dat je "{confirmDelete?.title}" wilt verwijderen?</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmDelete?.is_recurring && (
            <div className="space-y-2 py-2" data-testid="delete-scope-options">
              <label className="flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-muted/30 transition" data-testid="scope-single">
                <input type="radio" name="del-scope" value="single" checked={deleteScope === 'single'} onChange={() => setDeleteScope('single')} className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Alleen deze keer</div>
                  <div className="text-xs text-muted-foreground">Sla deze ene datum over, behoud de rest van de serie.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer hover:bg-muted/30 transition" data-testid="scope-series">
                <input type="radio" name="del-scope" value="series" checked={deleteScope === 'series'} onChange={() => setDeleteScope('series')} className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium">Hele serie</div>
                  <div className="text-xs text-muted-foreground">Verwijder alle herhalingen.</div>
                </div>
              </label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEvent} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Verwijderen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


/* ─── Event Dialog (create + edit) ─── */
function EventDialog({ open, onClose, event, onSaved }) {
  const isEdit = !!(event && event.id);
  const isRecurringInstance = !!(event && event.is_recurring);
  const [form, setForm] = useState({
    title: '', event_type: 'workshop', description: '', location: '',
    all_day: false, start_date: '', start_time: '09:00', end_date: '', end_time: '10:00',
    lead_id: '', property_id: '', is_private: false, color: '',
    recurrence_pattern: '', recurrence_count: 5,
    attendee_user_ids: [],
  });
  const [editScope, setEditScope] = useState('single');
  const [saving, setSaving] = useState(false);
  const [leads, setLeads] = useState([]);
  const [services, setServices] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    if (!open) return;
    setEditScope('single');
    if (isEdit) {
      const attendees = event.attendee_user_ids || [];
      setForm({
        title: event.title || '',
        event_type: event.event_type || 'workshop',
        description: event.description || '',
        location: event.location || '',
        all_day: !!event.all_day,
        start_date: toDateInputValue(event.start_at),
        start_time: toTimeInputValue(event.start_at) || '09:00',
        end_date: toDateInputValue(event.end_at),
        end_time: toTimeInputValue(event.end_at) || '10:00',
        lead_id: event.lead_id || '',
        property_id: event.property_id || '',
        is_private: !!event.is_private,
        color: event.color || '',
        recurrence_pattern: event.recurrence_pattern || '',
        recurrence_count: event.recurrence_count || 5,
        attendee_user_ids: attendees,
      });
    } else {
      const todayStr = toDateInputValue(new Date().toISOString());
      const presetDate = event && event.__prefillDate ? event.__prefillDate : todayStr;
      setForm({
        title: '', event_type: 'workshop', description: '', location: '',
        all_day: false,
        start_date: presetDate, start_time: '09:00',
        end_date: presetDate, end_time: '10:00',
        lead_id: '', property_id: '', is_private: false, color: '',
        recurrence_pattern: '', recurrence_count: 5,
        attendee_user_ids: [],
      });
    }
  }, [open, event, isEdit]);

  useEffect(() => {
    if (!open) return;
    leadsAPI.list({ limit: 200 }).then(r => setLeads(r.data.leads || [])).catch(() => {});
    propertiesAPI.list({ limit: 200 }).then(r => setServices(r.data.properties || [])).catch(() => {});
    teamAPI.members().then(r => setTeamMembers(r.data || [])).catch(() => {});
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Titel is verplicht'); return; }
    if (!form.start_date) { toast.error('Startdatum is verplicht'); return; }

    const startISO = form.all_day
      ? toISO(form.start_date, '00:00')
      : toISO(form.start_date, form.start_time);
    let endISO = null;
    if (form.all_day) {
      // Hele dag: end_at = einde van de gekozen dag (23:59) of einddatum als die er is
      const endDate = form.end_date || form.start_date;
      endISO = toISO(endDate, '23:59');
    } else {
      const endDate = form.end_date || form.start_date;
      endISO = toISO(endDate, form.end_time);
      if (endISO && startISO && new Date(endISO) < new Date(startISO)) {
        toast.error('Eindtijd moet na de starttijd zijn'); return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        event_type: form.event_type,
        description: form.description?.trim() || null,
        location: form.location?.trim() || null,
        all_day: form.all_day,
        start_at: startISO,
        end_at: endISO,
        lead_id: form.lead_id || null,
        property_id: form.property_id || null,
        is_private: form.is_private,
        color: form.color || null,
        recurrence_pattern: form.recurrence_pattern || null,
        recurrence_count: form.recurrence_pattern ? Math.max(2, Math.min(365, parseInt(form.recurrence_count, 10) || 2)) : null,
        attendee_user_ids: form.attendee_user_ids.length > 0 ? form.attendee_user_ids : null,
      };
      if (isEdit) {
        // Bij recurring instance: gebruik gekozen scope; anders altijd "series"
        const params = isRecurringInstance ? { scope: editScope } : undefined;
        await calendarAPI.updateEvent(event.id, payload, params);
        toast.success(isRecurringInstance && editScope === 'single' ? 'Deze instantie bijgewerkt' : 'Event bijgewerkt');
      } else {
        await calendarAPI.createEvent(payload);
        toast.success(payload.recurrence_pattern ? `Serie van ${payload.recurrence_count} events aangemaakt` : 'Event aangemaakt');
      }
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(', ') : (d || 'Opslaan mislukt'));
    } finally {
      setSaving(false);
    }
  };

  const typeCfg = EVENT_TYPE_CONFIG[form.event_type] || EVENT_TYPE_CONFIG.other;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Event bewerken' : 'Nieuw event'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          {isRecurringInstance && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="edit-scope-banner">
              <div className="flex items-center gap-1.5 text-xs font-medium text-amber-900">
                <Repeat className="w-3.5 h-3.5" /> Onderdeel van een terugkerende serie
              </div>
              <div className="space-y-1.5">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="edit-scope" value="single" checked={editScope === 'single'} onChange={() => setEditScope('single')} className="mt-0.5" data-testid="edit-scope-single" />
                  <span className="text-xs"><b>Alleen deze keer</b> — wijzigingen gelden alleen voor deze datum</span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="edit-scope" value="series" checked={editScope === 'series'} onChange={() => setEditScope('series')} className="mt-0.5" data-testid="edit-scope-series" />
                  <span className="text-xs"><b>Hele serie</b> — wijzigingen gelden voor alle herhalingen</span>
                </label>
              </div>
            </div>
          )}

          <div>
            <Label>Titel <span className="text-destructive">*</span></Label>
            <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="bijv. Workshop bij Acme BV" data-testid="event-title-input" />
          </div>

          {/* Type pills */}
          <div>
            <Label>Type</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {Object.entries(EVENT_TYPE_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                const active = form.event_type === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, event_type: key }))}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-all ${
                      active ? 'text-white border-transparent' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                    style={active ? { background: cfg.hex } : {}}
                    data-testid={`type-${key}`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* All-day switch */}
          <div className="flex items-center gap-3 py-1">
            <Switch checked={form.all_day} onCheckedChange={(v) => setForm(p => ({ ...p, all_day: v }))} id="all-day" data-testid="all-day-switch" />
            <Label htmlFor="all-day" className="cursor-pointer text-sm">Hele dag</Label>
          </div>

          {/* Date/time */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Startdatum *</Label>
              <Input type="date" value={form.start_date} onChange={(e) => setForm(p => ({ ...p, start_date: e.target.value }))} data-testid="start-date" />
            </div>
            {!form.all_day && (
              <div>
                <Label className="text-xs">Starttijd</Label>
                <Input type="time" value={form.start_time} onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))} data-testid="start-time" />
              </div>
            )}
            <div>
              <Label className="text-xs">Einddatum</Label>
              <Input type="date" value={form.end_date} onChange={(e) => setForm(p => ({ ...p, end_date: e.target.value }))} placeholder="zelfde als start" />
            </div>
            {!form.all_day && (
              <div>
                <Label className="text-xs">Eindtijd</Label>
                <Input type="time" value={form.end_time} onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))} />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" /> Locatie</Label>
            <Input value={form.location} onChange={(e) => setForm(p => ({ ...p, location: e.target.value }))} placeholder="bijv. Online, Amsterdam, kantoor klant" />
          </div>

          {/* Recurrence: niet beschikbaar bij bewerken van één instantie van een serie */}
          {!(isRecurringInstance && editScope === 'single') && (
            <div className="rounded-lg border border-border/60 p-3 space-y-2 bg-muted/20">
              <div className="flex items-center gap-1.5">
                <Repeat className="w-3.5 h-3.5 text-muted-foreground" />
                <Label className="text-xs">Herhalen</Label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select
                  value={form.recurrence_pattern}
                  onChange={(e) => setForm(p => ({ ...p, recurrence_pattern: e.target.value }))}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                  data-testid="recurrence-pattern"
                >
                  <option value="">Geen (eenmalig)</option>
                  <option value="daily">Elke dag</option>
                  <option value="weekly">Elke week</option>
                  <option value="monthly">Elke maand</option>
                </select>
                {form.recurrence_pattern && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={2}
                      max={365}
                      value={form.recurrence_count}
                      onChange={(e) => setForm(p => ({ ...p, recurrence_count: e.target.value }))}
                      className="h-9 w-20"
                      data-testid="recurrence-count"
                    />
                    <span className="text-xs text-muted-foreground">keer in totaal</span>
                  </div>
                )}
              </div>
              {form.recurrence_pattern && (
                <p className="text-[10px] text-muted-foreground">
                  Wordt {form.recurrence_count || 2}× herhaald, te beginnen op {form.start_date || 'de startdatum'}.
                </p>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Koppel aan dienst</Label>
              <select value={form.property_id || ''} onChange={(e) => setForm(p => ({ ...p, property_id: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                <option value="">Geen</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Koppel aan lead</Label>
              <select value={form.lead_id || ''} onChange={(e) => setForm(p => ({ ...p, lead_id: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                <option value="">Geen</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Beschrijving</Label>
            <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} rows={2} />
          </div>

          <div className="flex items-center gap-3 pt-1 border-t">
            <Switch checked={form.is_private} onCheckedChange={(v) => setForm(p => ({ ...p, is_private: v }))} id="private" data-testid="private-switch" />
            <Label htmlFor="private" className="cursor-pointer text-sm flex items-center gap-1">
              <Lock className="w-3 h-3" /> Privé (alleen jij ziet dit event)
            </Label>
          </div>

          {teamMembers.length > 0 && (
            <div className="pt-2 border-t">
              <Label className="text-xs flex items-center gap-1"><Users className="w-3 h-3" /> Deelnemers</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-2 max-h-32 overflow-y-auto">
                {teamMembers.map(m => {
                  const isSelected = form.attendee_user_ids.includes(m.user_id);
                  return (
                    <label key={m.user_id} className={`flex items-center gap-2 p-2 rounded-md cursor-pointer text-xs transition-colors ${isSelected ? 'bg-primary/10 border border-primary/30' : 'border border-transparent hover:bg-muted/50'}`}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm(p => ({ ...p, attendee_user_ids: [...p.attendee_user_ids, m.user_id] }));
                          } else {
                            setForm(p => ({ ...p, attendee_user_ids: p.attendee_user_ids.filter(id => id !== m.user_id) }));
                          }
                        }}
                        className="rounded border-input"
                      />
                      <span className="truncate">{m.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuleren</Button>
            <Button type="submit" disabled={saving} className="gap-1" data-testid="save-event-btn"
              style={{ background: typeCfg.hex }}>
              {saving ? 'Opslaan...' : (isEdit ? 'Bijwerken' : 'Aanmaken')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Mobile Week View ─── */
function MobileWeekView({ currentDate, selectedKey, todayKey, eventMap, onSelectDate, onPrevWeek, onNextWeek }) {
  const startOfWeek = new Date(currentDate);
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(d.getDate() + i);
    weekDays.push(d);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onPrevWeek} className="h-8 w-8 p-0"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-xs font-semibold text-muted-foreground">
          {weekDays[0].toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' })} — {weekDays[6].toLocaleDateString('nl-NL', { month: 'short', day: 'numeric' })}
        </span>
        <Button variant="ghost" size="sm" onClick={onNextWeek} className="h-8 w-8 p-0"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((d) => {
          const key = dateKey(d.getFullYear(), d.getMonth(), d.getDate());
          const events = eventMap[key] || [];
          const isToday = key === todayKey;
          const isSelected = key === selectedKey;
          return (
            <button
              key={key}
              onClick={() => onSelectDate({ year: d.getFullYear(), month: d.getMonth(), day: d.getDate() })}
              className={`flex flex-col items-center py-2 rounded-lg transition-colors ${
                isSelected ? 'bg-primary text-primary-foreground' :
                isToday ? 'bg-primary/10' :
                'hover:bg-muted'
              }`}
            >
              <span className="text-[9px] uppercase font-medium opacity-60">{DAYS[d.getDay()].slice(0, 1)}</span>
              <span className="text-sm font-semibold mt-0.5">{d.getDate()}</span>
              {events.length > 0 && (
                <div className="flex gap-0.5 mt-1">
                  {events.some(e => e.kind === 'event') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-emerald-500'}`} />}
                  {events.some(e => e.kind === 'follow_up') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-orange-400'}`} />}
                  {events.some(e => e.kind === 'activity') && <div className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-primary-foreground' : 'bg-blue-400'}`} />}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
